import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthPageLayout from '../components/AuthPageLayout';
import PasswordInput from '../components/PasswordInput';

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (success) {
      const redirectTo = searchParams.get('redirect') || '/';
      const timer = setTimeout(() => {
        navigate(redirectTo, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setLoading(true);

    try {
      await resetPassword({ token, password });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthPageLayout title="Password reset" description="Signing you in…">
        <div className="flex justify-center">
          <CheckCircle className="w-16 h-16 text-state-success" />
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Choose a new password"
      description="Enter a new password to finish resetting your account."
      error={error}
      footer={<Link to="/login" className="text-brand hover:text-brand-dark font-medium">Back to sign in</Link>}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <PasswordInput
          id="password"
          label="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          required
          minLength={8}
          autoFocus
          showPassword={showPassword}
          onToggleShow={() => setShowPassword(!showPassword)}
        />

        <PasswordInput
          id="confirmPassword"
          label="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
          required
          showPassword={showPassword}
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-brand hover:bg-brand-dark disabled:bg-brand/50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Resetting…
            </>
          ) : (
            'Reset password'
          )}
        </button>
      </form>
    </AuthPageLayout>
  );
}
